<template>
  <div class="storage-tab">
    <!-- Storage Type Selector -->
    <div class="storage-selector">
      <button
        class="storage-btn"
        :class="{ active: activeStorage === 'localStorage' }"
        @click="activeStorage = 'localStorage'"
      >
        localStorage
        <span class="count">{{ localStorageCount }}</span>
      </button>
      <button
        class="storage-btn"
        :class="{ active: activeStorage === 'sessionStorage' }"
        @click="activeStorage = 'sessionStorage'"
      >
        sessionStorage
        <span class="count">{{ sessionStorageCount }}</span>
      </button>
    </div>

    <!-- Filter -->
    <div class="filter-input">
      <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="14" height="14">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input type="text" v-model="filterText" placeholder="Filter by key..." />
    </div>

    <!-- Storage Items -->
    <div class="storage-list">
      <div
        v-for="item in filteredItems"
        :key="item.key"
        class="storage-item"
        :class="{ expanded: expandedKeys.includes(item.key) }"
      >
        <div class="item-header" @click="toggleExpand(item.key)">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            width="12"
            height="12"
            class="chevron"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
          <span class="item-key" :title="item.key">{{ item.key }}</span>
          <span class="item-type">{{ item.type }}</span>
          <button class="edit-btn" @click.stop="startEdit(item)" title="Edit value">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </button>
          <button class="delete-btn" @click.stop="deleteItem(item.key)" title="Delete">
            <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="12" height="12">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div v-if="editingKey === item.key" class="item-edit">
          <textarea v-model="editValue" class="edit-input" rows="5"></textarea>
          <div class="edit-actions">
            <button class="edit-action-btn" @click="saveEdit(item.key)">Save</button>
            <button class="edit-action-btn ghost" @click="cancelEdit">Cancel</button>
          </div>
        </div>
        <div v-if="expandedKeys.includes(item.key) && editingKey !== item.key" class="item-value">
          <pre>{{ item.displayValue }}</pre>
        </div>
      </div>

      <div v-if="filteredItems.length === 0" class="empty-state">
        <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="24" height="24">
          <rect width="20" height="5" x="2" y="3" rx="1" />
          <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
          <path d="M10 12h4" />
        </svg>
        <span>{{ filterText ? "No matching items" : "Storage is empty" }}</span>
      </div>
    </div>

    <!-- Actions -->
    <div class="storage-actions">
      <button class="action-btn" @click="refreshStorage">
        <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="12" height="12">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
        Refresh
      </button>
      <button class="action-btn danger" @click="clearCurrentStorage">
        <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="12" height="12">
          <path d="M3 6h18" />
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
        </svg>
        Clear {{ activeStorage === "localStorage" ? "Local" : "Session" }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";

interface StorageItem {
  key: string;
  value: string;
  type: string;
  displayValue: string;
}

const activeStorage = ref<"localStorage" | "sessionStorage">("localStorage");
const filterText = ref("");
const expandedKeys = ref<string[]>([]);
const editingKey = ref<string | null>(null);
const editValue = ref("");
const localStorageItems = ref<StorageItem[]>([]);
const sessionStorageItems = ref<StorageItem[]>([]);

const localStorageCount = computed(() => localStorageItems.value.length);
const sessionStorageCount = computed(() => sessionStorageItems.value.length);

const currentItems = computed(() => {
  return activeStorage.value === "localStorage" ? localStorageItems.value : sessionStorageItems.value;
});

const filteredItems = computed(() => {
  if (!filterText.value) return currentItems.value;
  const filter = filterText.value.toLowerCase();
  return currentItems.value.filter((item) => item.key.toLowerCase().includes(filter));
});

const parseStorageItem = (key: string, value: string): StorageItem => {
  let type = "string";
  let displayValue = value;

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === "object" && parsed !== null) {
      type = Array.isArray(parsed) ? "array" : "object";
      displayValue = JSON.stringify(parsed, null, 2);
    } else if (typeof parsed === "number") {
      type = "number";
    } else if (typeof parsed === "boolean") {
      type = "boolean";
    }
  } catch {
    // Keep as string
  }

  return { key, value, type, displayValue };
};

const loadStorage = (storage: Storage): StorageItem[] => {
  const items: StorageItem[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key) {
      const value = storage.getItem(key) || "";
      items.push(parseStorageItem(key, value));
    }
  }
  return items.sort((a, b) => a.key.localeCompare(b.key));
};

const refreshStorage = () => {
  localStorageItems.value = loadStorage(localStorage);
  sessionStorageItems.value = loadStorage(sessionStorage);
};

const toggleExpand = (key: string) => {
  const index = expandedKeys.value.indexOf(key);
  if (index === -1) {
    expandedKeys.value.push(key);
  } else {
    expandedKeys.value.splice(index, 1);
  }
};

const deleteItem = (key: string) => {
  const storage = activeStorage.value === "localStorage" ? localStorage : sessionStorage;
  storage.removeItem(key);
  refreshStorage();
};

const startEdit = (item: StorageItem) => {
  editingKey.value = item.key;
  editValue.value = item.type === "object" || item.type === "array" ? item.displayValue : item.value;
  if (!expandedKeys.value.includes(item.key)) {
    expandedKeys.value.push(item.key);
  }
};

const cancelEdit = () => {
  editingKey.value = null;
  editValue.value = "";
};

const saveEdit = (key: string) => {
  const storage = activeStorage.value === "localStorage" ? localStorage : sessionStorage;
  const trimmed = editValue.value.trim();
  let valueToStore = editValue.value;

  if (trimmed) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === "object") {
        valueToStore = JSON.stringify(parsed);
      }
    } catch {
      // Keep as string
    }
  }

  storage.setItem(key, valueToStore);
  refreshStorage();
  cancelEdit();
};

const clearCurrentStorage = () => {
  if (confirm(`Are you sure you want to clear all ${activeStorage.value} items?`)) {
    const storage = activeStorage.value === "localStorage" ? localStorage : sessionStorage;
    storage.clear();
    refreshStorage();
  }
};

onMounted(() => {
  refreshStorage();
});

watch(activeStorage, () => {
  cancelEdit();
});
</script>

<style scoped>
.storage-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Critical for flex child scrolling */
  gap: 12px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.storage-tab::-webkit-scrollbar {
  width: 6px;
}

.storage-tab::-webkit-scrollbar-track {
  background: transparent;
}

.storage-tab::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

/* Storage Selector */
.storage-selector {
  display: flex;
  gap: 6px;
}

.storage-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #9090a0;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.storage-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #c0c0d0;
}

.storage-btn.active {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.count {
  font-size: 10px;
  padding: 1px 5px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.storage-btn.active .count {
  background: rgba(59, 130, 246, 0.3);
}

/* Filter Input */
.filter-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #8080a0;
}

.filter-input input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 12px;
  color: #e0e0f0;
}

.filter-input input::placeholder {
  color: #707090;
}

/* Storage List */
.storage-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.storage-item {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  overflow: hidden;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.item-header:hover {
  background: rgba(255, 255, 255, 0.02);
}

.chevron {
  color: #707090;
  transition: transform 0.15s ease;
  flex-shrink: 0;
}

.storage-item.expanded .chevron {
  transform: rotate(90deg);
}

.item-key {
  flex: 1;
  min-width: 0; /* Allow text truncation in flex child */
  font-size: 12px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #bb9af7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-type {
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  color: #8090a0;
}

.edit-btn,
.delete-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #707090;
  cursor: pointer;
  border-radius: 4px;
  opacity: 0;
  transition: all 0.15s ease;
}

.item-header:hover .edit-btn,
.item-header:hover .delete-btn {
  opacity: 1;
}

.edit-btn:hover {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.item-edit {
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-input {
  width: 100%;
  min-height: 90px;
  resize: vertical;
  font-size: 11px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #e0e0f0;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 8px;
  outline: none;
}

.edit-input:focus {
  border-color: rgba(59, 130, 246, 0.4);
}

.edit-actions {
  display: flex;
  gap: 8px;
}

.edit-action-btn {
  flex: 0 0 auto;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 500;
  color: #e0e0f0;
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.edit-action-btn:hover {
  background: rgba(59, 130, 246, 0.3);
}

.edit-action-btn.ghost {
  color: #b0b0c0;
  background: transparent;
  border-color: rgba(255, 255, 255, 0.1);
}

.edit-action-btn.ghost:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e0e0f0;
}
.item-value {
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.item-value pre {
  margin: 0;
  font-size: 11px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #7dd3fc;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 150px;
  overflow-y: auto;
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #707090;
  padding: 32px;
}

.empty-state svg {
  opacity: 0.5;
}

.empty-state span {
  font-size: 13px;
}

/* Actions */
.storage-actions {
  display: flex;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 1;
  background: rgba(15, 15, 26, 0.9);
  backdrop-filter: blur(6px);
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #b0b0c0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e0e0f0;
}

.action-btn.danger {
  border-color: rgba(239, 68, 68, 0.2);
}

.action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}
</style>


